import { WModalButton } from "@/components/button/WToolButton.tsx";
import { FormField } from "@/components/forms/FormField.tsx";
import { IconRadio } from "@/components/forms/IconRadio.tsx";
import { RadioGroupDiv } from "@/components/forms/RadioGroupDiv.tsx";
import { WRadioGroup } from "@/components/forms/WRadioGroup.tsx";
import { WModal, WModalContent } from "@/components/modal/WModal.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { reinstallTypes } from "@/modules/compute/labels.tsx";
import { OsVersionSelectCustom } from "@/modules/compute/os/OsVersionSelect.tsx";
import { type ComputeImageType, getDefaultOs, getResourceTypeImages, osIsWindows } from "@/modules/compute/os/os.ts";
import { availableImagesAtom } from "@/modules/compute/vmImagesQuery.ts";
import { reinstallVmMutation, type VirtualMachineLoc } from "@/modules/compute/vmQuery.ts";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { useGenerateUuid } from "@/utils/useGenerateUuid.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { useState } from "react";

export function ReinstallModalContent({ vm }: { vm: VirtualMachineLoc }) {
    const { t } = useLingui();
    const allImages = useSuspenseQueryAtom(availableImagesAtom);
    const reinstallMutation = useStandardMutation(reinstallVmMutation);
    const requestUuid = useGenerateUuid(undefined);

    const { uuid, name, location } = vm ?? {};
    const defaultType = reinstallTypes[0].id;
    const [type, setType] = useState<ComputeImageType>(defaultType);
    const [os, setOs] = useState(getDefaultOsForType(defaultType));

    // TODO: Somehow share OS selection logic with VmCreate - not currently easily possible due to RHF being used there

    function onChangeType(type: ComputeImageType) {
        setType(type);

        // Reset OS when type changes
        setOs(getDefaultOsForType(type));
    }

    function getImages(type: ComputeImageType) {
        const images = getResourceTypeImages(allImages, type);
        return type === "plain_os" ? images.filter((img) => !osIsWindows(img.os_name)) : images;
    }

    function getDefaultOsForType(type: ComputeImageType) {
        return getDefaultOs(getImages(type));
    }

    return (
        <WModalContent
            title={t`Reinstall`}
            label={t`Reinstall`}
            modalAction={async () => {
                await reinstallMutation.mutateAsync({ body: { uuid, ...os }, location, requestUuid });
            }}
        >
            <p>
                <Trans>
                    Reinstalling "{name}" will destroy all data on the boot disk and set it to the initial clean OS
                    state. All snapshots and backups are preserved.
                </Trans>
            </p>

            <WRadioGroup block label={t`Type`} isRequired value={type} onChange={onChangeType}>
                {reinstallTypes.map((item) => (
                    <IconRadio key={item.id} {...item} />
                ))}
            </WRadioGroup>

            <FormField block wide label={t`OS`} isRequired>
                <RadioGroupDiv>
                    <OsVersionSelectCustom items={getImages(type)} value={os} onChange={setOs} />
                </RadioGroupDiv>
            </FormField>
        </WModalContent>
    );
}

export function ReinstallModal({ vm }: { vm: VirtualMachineLoc }) {
    const { t } = useLingui();
    const { os_name } = vm ?? {};

    return (
        <WModal button={<WModalButton label={t`Reinstall`} icon="jp-retweet-icon" isDisabled={osIsWindows(os_name)} />}>
            <ReinstallModalContent vm={vm} />
        </WModal>
    );
}
